import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  LayersControl,
  useMap,
  useMapEvents,
  CircleMarker,
} from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import { projectsApi, ProjectLocation, locationsCache } from "@services/api/projects";
import { useDataStore } from "@store/user_org";
import { navigateTo } from "context/navigation.service";
import { showNotification } from "context/notification.service";
import Icon1 from "@assets/icons/Icon";

// Fix default marker icons broken by webpack
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

interface Props {
  onClose: () => void;
}

type LatLng = [number, number];
type SidebarTab = "located" | "unlocated";
type PlacementMode = {
  projectId: string;
  projectName: string;
  isEdit: boolean;
} | null;

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "current", label: "En cours" },
  { value: "finished", label: "Terminé" },
];

// ---------------------------------------------------------------------------
// MapImperative — sits inside MapContainer and handles anything that needs
// `useMap` / `useMapEvents`: auto-fit on filter change, fly-to on row click,
// hover highlight overlay, and click-to-place in placement mode.
// ---------------------------------------------------------------------------

function MapImperative({
  fitPoints,
  focusPosition,
  panPosition,
  hoveredPosition,
  placementActive,
  onPlace,
  skipNextFitRef,
}: {
  fitPoints: LatLng[];
  focusPosition: LatLng | null;
  panPosition: LatLng | null;
  hoveredPosition: LatLng | null;
  placementActive: boolean;
  onPlace: (lat: number, lng: number) => void;
  skipNextFitRef: React.MutableRefObject<boolean>;
}) {
  const map = useMap();

  // Auto-fit when the set of visible markers changes, unless the parent
  // told us to skip (e.g. right after a placement, where we want to keep
  // the user's current zoom/center).
  useEffect(() => {
    if (skipNextFitRef.current) {
      skipNextFitRef.current = false;
      return;
    }
    if (fitPoints.length === 0) return;
    if (fitPoints.length === 1) {
      map.setView(fitPoints[0], 13);
      return;
    }
    const bounds = L.latLngBounds(fitPoints);
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 13 });
  }, [fitPoints, map, skipNextFitRef]);

  // Fly to a single position on row click (animated, zoom in if needed).
  useEffect(() => {
    if (focusPosition) {
      map.flyTo(focusPosition, Math.max(map.getZoom(), 14), { duration: 0.8 });
    }
  }, [focusPosition, map]);

  // Instant recenter without changing zoom — used when entering edit mode
  // so the user sees the existing pin without the map jumping around.
  useEffect(() => {
    if (panPosition) {
      map.setView(panPosition, map.getZoom(), { animate: false });
    }
  }, [panPosition, map]);

  // In placement mode, a click anywhere on the map commits the new coord.
  useMapEvents({
    click(e) {
      if (placementActive) {
        onPlace(e.latlng.lat, e.latlng.lng);
      }
    },
  });

  return (
    <>
      {hoveredPosition && (
        <CircleMarker
          center={hoveredPosition}
          radius={22}
          pathOptions={{
            color: "#3b82f6",
            weight: 3,
            fillColor: "#3b82f6",
            fillOpacity: 0.15,
          }}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

export default function ProjectsMapModal({ onClose }: Props) {
  const orgMembers = useDataStore((s) => s.orgMembers);

  const [locations, setLocations] = useState<ProjectLocation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState("");
  const [conductorId, setConductorId] = useState<string>("");
  const [commercialId, setCommercialId] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");

  // Sidebar
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sidebarTab, setSidebarTab] = useState<SidebarTab>("located");

  // Interaction state
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [focusPosition, setFocusPosition] = useState<LatLng | null>(null);
  const [panPosition, setPanPosition] = useState<LatLng | null>(null);
  const [placementMode, setPlacementMode] = useState<PlacementMode>(null);
  const [saving, setSaving] = useState(false);

  // When true, the next auto-fit pass in MapImperative is skipped. Set by
  // handlePlace so a successful placement doesn't recenter/rezoom the map
  // — the user was looking at exactly where they clicked.
  const skipNextFitRef = useRef(false);

  // Fetch on open with stale-while-revalidate. Backend handles scoping.
  // Seed instantly from the in-memory cache for a snappy open (no spinner),
  // then always refetch in the background so the data stays fresh — the cache
  // is invalidated whenever a project moves, so this also catches any change.
  useEffect(() => {
    let cancelled = false;
    const seed = locationsCache.get();
    if (seed) {
      setLocations(seed);
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }
    setError(null);
    projectsApi
      .listLocations()
      .then((data) => {
        if (cancelled) return;
        // On a background refresh we already showed cached pins — don't let
        // the freshly-built fitPoints re-fit and yank the user's current view.
        if (seed) skipNextFitRef.current = true;
        setLocations(data);
        locationsCache.set(data);
      })
      .catch(() => {
        // Keep showing cached pins if a background refresh fails; only surface
        // an error when we had nothing to show in the first place.
        if (!cancelled && !seed) setError("Impossible de charger la carte");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Close on ESC — unless we're in placement mode, in which case ESC
  // cancels placement instead of closing the whole modal.
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (placementMode) {
        setPlacementMode(null);
      } else {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose, placementMode]);

  // Dropdown options
  const conductors = useMemo(() => {
    const ids = new Set(
      locations.map((l) => l.conductor_id).filter(Boolean) as string[],
    );
    return orgMembers.filter((m) => ids.has(m.id));
  }, [locations, orgMembers]);

  const commercials = useMemo(() => {
    const ids = new Set(
      locations.map((l) => l.commercial_id).filter(Boolean) as string[],
    );
    return orgMembers.filter((m) => ids.has(m.id));
  }, [locations, orgMembers]);

  // Apply active filters
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return locations.filter((loc) => {
      if (conductorId && loc.conductor_id !== conductorId) return false;
      if (commercialId && loc.commercial_id !== commercialId) return false;
      if (statusFilter && loc.status !== statusFilter) return false;
      if (q) {
        const hay = `${loc.name} ${loc.city} ${loc.num}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [locations, search, conductorId, commercialId, statusFilter]);

  const located = useMemo(
    () =>
      filtered.filter(
        (l) =>
          l.coordinates?.latitude != null && l.coordinates?.longitude != null,
      ),
    [filtered],
  );

  const unlocated = useMemo(
    () =>
      filtered.filter(
        (l) =>
          !(l.coordinates?.latitude != null && l.coordinates?.longitude != null),
      ),
    [filtered],
  );

  const fitPoints = useMemo<LatLng[]>(
    () => located.map((l) => [l.coordinates!.latitude, l.coordinates!.longitude]),
    [located],
  );

  // Resolve hovered → position for the CircleMarker overlay
  const hoveredPosition = useMemo<LatLng | null>(() => {
    if (!hoveredId) return null;
    const loc = locations.find((l) => l.id === hoveredId);
    if (!loc?.coordinates?.latitude || !loc?.coordinates?.longitude) return null;
    return [loc.coordinates.latitude, loc.coordinates.longitude];
  }, [hoveredId, locations]);

  const hasActiveFilters =
    search.trim() !== "" ||
    conductorId !== "" ||
    commercialId !== "" ||
    statusFilter !== "";

  const clearFilters = () => {
    setSearch("");
    setConductorId("");
    setCommercialId("");
    setStatusFilter("");
  };

  const memberName = (uid: string | null | undefined) => {
    if (!uid) return "";
    const m = orgMembers.find((u) => u.id === uid);
    return m?.infos?.display_name || m?.infos?.full_name || "";
  };

  const openProject = (id: string) => {
    navigateTo({ route: `/project/${id}` });
    onClose();
  };

  const focusOnProject = (loc: ProjectLocation) => {
    if (loc.coordinates?.latitude != null && loc.coordinates?.longitude != null) {
      setFocusPosition([loc.coordinates.latitude, loc.coordinates.longitude]);
    }
  };

  const startPlacement = (loc: ProjectLocation) => {
    const isEdit =
      loc.coordinates?.latitude != null && loc.coordinates?.longitude != null;
    setPlacementMode({
      projectId: loc.id,
      projectName: loc.name || "(sans nom)",
      isEdit,
    });
    // When editing, recenter on the current pin without touching zoom —
    // the user asked for no animation and no zoom change on edit entry.
    // Passing a fresh array every time so the effect reliably re-fires.
    if (isEdit && loc.coordinates) {
      setPanPosition([loc.coordinates.latitude, loc.coordinates.longitude]);
      setHoveredId(loc.id);
    }
  };

  // Called by MapImperative when the user clicks the map in placement mode
  const handlePlace = useCallback(
    async (lat: number, lng: number) => {
      if (!placementMode || saving) return;
      setSaving(true);
      try {
        // Backend accepts a top-level `coordinates` field that it maps to
        // infos.general.coordinates via a dot-path update. Sending the
        // nested {infos:{general:{coordinates}}} shape would trigger full
        // ProjectInfos validation and demand name/num.
        await projectsApi.update(placementMode.projectId, {
          coordinates: { latitude: lat, longitude: lng },
        });
        // Tell the map to skip its next auto-fit pass — we don't want the
        // view to jump after the user clicked exactly where they meant.
        skipNextFitRef.current = true;
        setLocations((prev) => {
          const next = prev.map((l) =>
            l.id === placementMode.projectId
              ? { ...l, coordinates: { latitude: lat, longitude: lng } }
              : l,
          );
          // projectsApi.update() just invalidated the cache; repopulate it with
          // the new coords so the next open is both instant and correct.
          locationsCache.set(next);
          return next;
        });
        showNotification({
          type: "success",
          message: placementMode.isEdit
            ? "Emplacement mis à jour"
            : "Projet placé sur la carte",
        });
        setPlacementMode(null);
        // Deliberately don't fly-to / re-zoom — the user clicked exactly
        // where they wanted the pin and is already looking at it. Just
        // briefly highlight it so they can confirm visually.
        setSidebarTab("located");
        setHoveredId(placementMode.projectId);
        setTimeout(() => {
          setHoveredId((id) => (id === placementMode.projectId ? null : id));
        }, 2500);
      } catch {
        showNotification({
          type: "error",
          message: "Erreur lors de l'enregistrement",
        });
      } finally {
        setSaving(false);
      }
    },
    [placementMode, saving],
  );

  // Switch to the unlocated tab automatically if all current located items
  // have been dealt with OR if there are unlocated items but nothing located.
  // Also default to a sensible initial tab on first load.
  const firstRenderRef = useRef(true);
  useEffect(() => {
    if (firstRenderRef.current && !isLoading && locations.length > 0) {
      firstRenderRef.current = false;
      if (located.length === 0 && unlocated.length > 0) {
        setSidebarTab("unlocated");
      }
    }
  }, [isLoading, locations.length, located.length, unlocated.length]);

  const sidebarItems = sidebarTab === "located" ? located : unlocated;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        style={{ width: "95vw", height: "88vh", maxWidth: 1400 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex flex-row items-center justify-between px-5 py-3 border-b border-neutral-200">
          <div className="flex flex-row items-center gap-2">
            <button
              onClick={() => setSidebarOpen((v) => !v)}
              className="p-1 rounded-md text-neutral-500 hover:text-neutral-800 hover:bg-neutral-100 transition-colors"
              title={sidebarOpen ? "Masquer la liste" : "Afficher la liste"}
              aria-label={sidebarOpen ? "Masquer la liste" : "Afficher la liste"}
            >
              <Icon1 name="menu" size={18} />
            </button>
            <Icon1 name="location" size={20} />
            <span className="text-h2 font-semibold text-neutral-700">
              Carte des projets
            </span>
            <span className="text-h3 text-neutral-400 ml-1">
              {isLoading
                ? "· chargement…"
                : `· ${located.length} localisé${located.length > 1 ? "s" : ""}`}
              {unlocated.length > 0 && !isLoading && (
                <> · {unlocated.length} sans coordonnées</>
              )}
            </span>
          </div>
          <button
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-700 transition-colors"
          >
            <Icon1 name="close" size={20} />
          </button>
        </div>

        {/* Filter bar */}
        <div className="flex flex-col gap-2 px-5 py-3 border-b border-neutral-200 bg-neutral-50">
          <div className="flex flex-row gap-2 items-center">
            <div className="flex flex-row items-center flex-1 max-w-500 bg-white rounded-lg border border-neutral-200 px-3 py-2 gap-2">
              <Icon1 name="search" size={14} color="#9ca3af" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher un projet, une ville, un numéro…"
                className="flex-1 bg-transparent outline-none text-h4 text-neutral-700 placeholder:text-neutral-400"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="text-neutral-400 hover:text-neutral-700"
                >
                  <Icon1 name="close" size={12} />
                </button>
              )}
            </div>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-h4 text-neutral-500 hover:text-neutral-700 underline whitespace-nowrap"
              >
                Réinitialiser
              </button>
            )}
          </div>

          <div className="flex flex-row gap-2 items-center flex-wrap">
            <FilterDropdown
              label="Conducteur"
              value={conductorId}
              options={conductors.map((c) => ({
                value: c.id,
                label:
                  c.infos?.display_name || c.infos?.full_name || c.infos?.email || c.id,
              }))}
              onChange={setConductorId}
            />
            <FilterDropdown
              label="Commercial"
              value={commercialId}
              options={commercials.map((c) => ({
                value: c.id,
                label:
                  c.infos?.display_name || c.infos?.full_name || c.infos?.email || c.id,
              }))}
              onChange={setCommercialId}
            />
            <FilterDropdown
              label="Statut"
              value={statusFilter}
              options={STATUS_OPTIONS}
              onChange={setStatusFilter}
            />
          </div>
        </div>

        {/* Body: map + sidebar. Grid with minmax(0, 1fr) avoids the flexbox
            shrink issues that were squishing the sidebar. Explicit columns
            guarantee the map and sidebar each get their own height context. */}
        <div
          className="flex-1 overflow-hidden min-h-0 grid"
          style={{
            gridTemplateColumns:
              sidebarOpen && !isLoading && !error
                ? "minmax(0, 1fr) 20rem"
                : "minmax(0, 1fr)",
            gridTemplateRows: "minmax(0, 1fr)",
          }}
        >
          {/* Map column */}
          <div className="relative min-h-0 min-w-0">
            {placementMode && (
              <div
                className={`absolute top-0 left-0 right-0 z-[1000] px-5 py-3 shadow-md flex items-center gap-4 ${
                  placementMode.isEdit ? "bg-amber-500" : "bg-blue-600"
                } text-white`}
              >
                <Icon1 name="location" size={18} color="#ffffff" />
                <div className="flex flex-col flex-1 min-w-0">
                  <div className="text-h3 font-semibold truncate">
                    {placementMode.isEdit
                      ? `Déplacement de « ${placementMode.projectName} »`
                      : `Placement de « ${placementMode.projectName} »`}
                  </div>
                  <div className="text-h5 opacity-90 truncate">
                    {saving
                      ? "Enregistrement…"
                      : placementMode.isEdit
                      ? "Cliquez n'importe où sur la carte pour déplacer le marqueur à un nouvel endroit."
                      : "Cliquez sur la carte à l'emplacement exact du projet."}
                  </div>
                </div>
                {!saving && (
                  <button
                    onClick={() => setPlacementMode(null)}
                    className="flex-shrink-0 bg-white/15 hover:bg-white/25 text-white text-h5 px-3 py-1 rounded-md transition-colors"
                  >
                    Annuler (Échap)
                  </button>
                )}
              </div>
            )}
            {isLoading ? (
              <div className="flex flex-col items-center justify-center h-full gap-2 text-neutral-400">
                <span className="text-h3">Chargement de la carte…</span>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center h-full gap-2 text-red-500">
                <Icon1 name="warning" size={30} />
                <span className="text-h3">{error}</span>
              </div>
            ) : (
              <div
                className="h-full w-full"
                style={{ cursor: placementMode ? "crosshair" : undefined }}
              >
                <MapContainer
                  center={[46.8, 2.3]}
                  zoom={6}
                  style={{ width: "100%", height: "100%" }}
                >
                  <LayersControl position="topright">
                    <LayersControl.BaseLayer checked name="Satellite">
                      <TileLayer
                        attribution='&copy; <a href="https://www.esri.com/">Esri</a>, Maxar, Earthstar Geographics'
                        url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                        maxZoom={19}
                      />
                    </LayersControl.BaseLayer>
                    <LayersControl.BaseLayer name="Plan">
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a>'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                    </LayersControl.BaseLayer>
                  </LayersControl>
                  <MapImperative
                    fitPoints={fitPoints}
                    focusPosition={focusPosition}
                    panPosition={panPosition}
                    hoveredPosition={hoveredPosition}
                    placementActive={!!placementMode && !saving}
                    onPlace={handlePlace}
                    skipNextFitRef={skipNextFitRef}
                  />
                  <MarkerClusterGroup chunkedLoading>
                    {located.map((loc) => (
                      <Marker
                        key={loc.id}
                        position={[
                          loc.coordinates!.latitude,
                          loc.coordinates!.longitude,
                        ]}
                        eventHandlers={{
                          mouseover: () => setHoveredId(loc.id),
                          mouseout: () =>
                            setHoveredId((id) => (id === loc.id ? null : id)),
                        }}
                      >
                        <Popup>
                          <div className="flex flex-col gap-1 min-w-[200px]">
                            <span className="font-semibold text-neutral-800">
                              {loc.name || "(sans nom)"}
                            </span>
                            {loc.city && (
                              <span className="text-neutral-500 text-sm">
                                {loc.city}
                              </span>
                            )}
                            {loc.conductor_id && (
                              <span className="text-neutral-400 text-xs">
                                Conducteur : {memberName(loc.conductor_id) || "—"}
                              </span>
                            )}
                            <div className="flex flex-row gap-3 mt-1">
                              <button
                                onClick={() => openProject(loc.id)}
                                className="text-sm text-blue-600 hover:underline text-left"
                              >
                                Ouvrir →
                              </button>
                              <button
                                onClick={() => startPlacement(loc)}
                                className="text-sm text-neutral-500 hover:text-neutral-800 hover:underline text-left"
                              >
                                Modifier l'emplacement
                              </button>
                            </div>
                          </div>
                        </Popup>
                      </Marker>
                    ))}
                  </MarkerClusterGroup>
                </MapContainer>
              </div>
            )}
          </div>

          {/* Sidebar */}
          {sidebarOpen && !isLoading && !error && (
            <div className="border-l border-neutral-200 bg-neutral-50 flex flex-col overflow-hidden min-h-0">
              {/* Tabs */}
              <div className="flex flex-row border-b border-neutral-200 bg-white">
                <SidebarTabButton
                  label="Sur la carte"
                  count={located.length}
                  active={sidebarTab === "located"}
                  onClick={() => setSidebarTab("located")}
                />
                <SidebarTabButton
                  label="Sans coordonnées"
                  count={unlocated.length}
                  active={sidebarTab === "unlocated"}
                  onClick={() => setSidebarTab("unlocated")}
                />
              </div>

              {/* List — flex-1 + min-h-0 make it the flex column's scrollable region */}
              <div className="flex-1 min-h-0 overflow-auto divide-y divide-neutral-100">
                {sidebarItems.length === 0 ? (
                  <div className="text-h4 text-neutral-400 py-8 text-center px-4">
                    {sidebarTab === "located"
                      ? hasActiveFilters
                        ? "Aucun projet localisé ne correspond aux filtres."
                        : "Aucun projet localisé."
                      : "Tout est localisé 🎉"}
                  </div>
                ) : (
                  sidebarItems.map((loc) => {
                    const isHovered = hoveredId === loc.id;
                    return (
                      <div
                        key={loc.id}
                        onMouseEnter={() => setHoveredId(loc.id)}
                        onMouseLeave={() =>
                          setHoveredId((id) => (id === loc.id ? null : id))
                        }
                        onClick={() => focusOnProject(loc)}
                        className={`flex flex-col items-start gap-0.5 px-4 py-3 cursor-pointer transition-colors ${
                          isHovered ? "bg-blue-50" : "hover:bg-white"
                        }`}
                      >
                        <div className="flex flex-row items-start justify-between w-full gap-2">
                          <div className="flex flex-col flex-1 min-w-0">
                            <div className="text-h4 font-medium text-neutral-800 truncate">
                              {loc.name || "(sans nom)"}
                            </div>
                            <div className="text-h5 text-neutral-400 truncate">
                              {loc.city || "—"}
                              {loc.num ? ` · ${loc.num}` : ""}
                            </div>
                            {loc.conductor_id && (
                              <div className="text-h5 text-neutral-400 truncate">
                                {memberName(loc.conductor_id)}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-row gap-1.5 mt-2 w-full">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              startPlacement(loc);
                            }}
                            className={`flex-1 flex items-center justify-center gap-1 px-2 py-1 rounded-md text-h5 font-medium transition-colors ${
                              sidebarTab === "located"
                                ? "bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200"
                                : "bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200"
                            }`}
                            title={
                              sidebarTab === "located"
                                ? "Déplacer ce projet sur la carte"
                                : "Placer ce projet sur la carte"
                            }
                          >
                            <Icon1 name="location" size={11} />
                            {sidebarTab === "located" ? "Déplacer" : "Placer"}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openProject(loc.id);
                            }}
                            className="flex-1 flex items-center justify-center gap-1 px-2 py-1 rounded-md text-h5 font-medium text-neutral-600 hover:bg-neutral-100 border border-neutral-200 transition-colors"
                            title="Ouvrir le projet"
                          >
                            Ouvrir
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Sidebar tab button
// ---------------------------------------------------------------------------

function SidebarTabButton({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 px-3 py-3 text-h4 font-medium border-b-2 transition-colors ${
        active
          ? "border-neutral-800 text-neutral-800"
          : "border-transparent text-neutral-500 hover:text-neutral-700"
      }`}
    >
      <span>{label}</span>
      <span
        className={`ml-1.5 text-h5 ${
          active ? "text-neutral-500" : "text-neutral-400"
        }`}
      >
        ({count})
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// FilterDropdown — pill-styled native select
// ---------------------------------------------------------------------------

function FilterDropdown({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
}) {
  const active = value !== "";
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`appearance-none pl-3 pr-8 py-1.5 rounded-full border text-h5 cursor-pointer transition-colors ${
          active
            ? "bg-neutral-800 text-white border-neutral-800"
            : "bg-white text-neutral-600 border-neutral-200 hover:border-neutral-400"
        }`}
      >
        <option value="">{label}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <svg
        className={`w-3 h-3 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none ${
          active ? "text-white" : "text-neutral-400"
        }`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
      </svg>
    </div>
  );
}
